// src/components/Navbar.js
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaBars,
  FaTimes,
  FaExternalLinkAlt,
  FaGraduationCap,
} from "react-icons/fa";

function Navbar() {
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navItems = useMemo(
    () => [
      { to: "/", label: "Home" },
      { to: "/about", label: "About" },
      { to: "/programs", label: "Programs" },
      { to: "/admissions", label: "Admissions" },
      { to: "/achievements", label: "Achievements" },
      { to: "/blog", label: "Blog" },
      { to: "/partners", label: "Partners" },
      { to: "/contact", label: "Contact" },
    ],
    []
  );

  const closeNav = () => setNavOpen(false);

  // Close mobile nav on route change
  useEffect(() => {
    closeNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Subtle scroll styling like a premium header
  useEffect(() => {
    const onScroll = () => setScrolled((window.scrollY || 0) > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <nav
        className="navbar navbar-expand-lg fixed-top"
        role="navigation"
        aria-label="Main navigation"
        style={{
          fontFamily:
            "'Inter', 'Segoe UI', Roboto, system-ui, -apple-system, sans-serif",
          background: scrolled ? "rgba(255,255,255,0.82)" : "rgba(255,255,255,0.62)",
          borderBottom: scrolled ? "1px solid rgba(15, 23, 42, 0.08)" : "1px solid rgba(15, 23, 42, 0.06)",
          boxShadow: scrolled ? "0 18px 50px rgba(15, 23, 42, 0.08)" : "0 10px 30px rgba(15, 23, 42, 0.05)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          transition: "background 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
        }}
      >
        <div className="container d-flex align-items-center">
          {/* Brand */}
          <Link
            className="navbar-brand d-flex align-items-center gap-2 me-3"
            to="/"
            onClick={closeNav}
            aria-label="Go to home"
            style={{ textDecoration: "none" }}
          >
            <span
              className="brand-mark"
              aria-hidden="true"
              style={{
                width: 40,
                height: 40,
                borderRadius: 14,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                  "linear-gradient(135deg, rgba(13,110,253,0.16), rgba(11,59,145,0.18))",
                border: "1px solid rgba(13,110,253,0.18)",
                boxShadow: "0 12px 28px rgba(13,110,253,0.10)",
                overflow: "hidden",
              }}
            >
              <img
                src="/iblogo.png"
                alt="IBG Logo"
                width="28"
                height="28"
                style={{ display: "block", objectFit: "contain" }}
              />
            </span>

            <div className="d-flex flex-column lh-sm">
              <span
                className="brand-title"
                style={{
                  fontWeight: 900,
                  letterSpacing: "-0.02em",
                  color: "var(--ibg-ink)",
                  fontSize: "0.98rem",
                  lineHeight: 1.05,
                }}
              >
                IBG
              </span>
              <span
                className="brand-subtitle"
                style={{
                  fontWeight: 700,
                  color: "var(--ibg-slate-soft)",
                  fontSize: "0.78rem",
                  letterSpacing: "0.01em",
                }}
              >
                Institute of Bankers
              </span>
            </div>
          </Link>

          {/* Toggler */}
          <button
            className="navbar-toggler border-0"
            type="button"
            aria-controls="navbarNav"
            aria-expanded={navOpen}
            aria-label="Toggle navigation menu"
            onClick={() => setNavOpen((s) => !s)}
            style={{
              marginLeft: "auto",
              width: 44,
              height: 44,
              borderRadius: 14,
              background: "rgba(15, 23, 42, 0.04)",
              boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
            }}
          >
            <span className="d-inline-flex align-items-center justify-content-center">
              {navOpen ? <FaTimes /> : <FaBars />}
            </span>
          </button>

          {/* Collapse */}
          <div
            className={`collapse navbar-collapse justify-content-end ${navOpen ? "show" : ""}`}
            id="navbarNav"
          >
            <div className="navbar-nav nav-right align-items-lg-center">
              {navItems.map((item) => {
                const isActive = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    className={`nav-link px-3 ${isActive ? "active" : ""}`}
                    to={item.to}
                    onClick={closeNav}
                    aria-current={isActive ? "page" : undefined}
                    style={{
                      fontWeight: 800,
                      color: isActive ? "var(--ibg-navy)" : "var(--ibg-ink)",
                      letterSpacing: "0.01em",
                      borderRadius: 14,
                    }}
                  >
                    {item.label}
                    {isActive && (
                      <span
                        aria-hidden="true"
                        style={{
                          display: "block",
                          height: 2,
                          borderRadius: 999,
                          marginTop: 6,
                          background:
                            "linear-gradient(90deg, var(--ibg-navy) 0%, var(--ibg-blue) 60%, rgba(102,178,255,0.9) 100%)",
                        }}
                      />
                    )}
                  </Link>
                );
              })}

              {/* Portal button */}
              <a
                className="nav-link portal-btn-wrapper px-3"
                href="https://portal.ibgambia.com"
                target="_blank"
                rel="noopener noreferrer"
                title="Open Student Portal (opens in new tab)"
                onClick={closeNav}
                style={{ textDecoration: "none" }}
              >
                <span className="portal-btn" role="button" aria-label="Open Student Portal">
                  <FaGraduationCap className="me-2" />
                  Portal
                  <FaExternalLinkAlt className="ms-2" style={{ opacity: 0.9, fontSize: "0.9em" }} />
                </span>
              </a>
            </div>
          </div>
        </div>

        {/* Mobile overlay (premium feel) */}
        <AnimatePresence>
          {navOpen && (
            <motion.div
              className="nav-overlay d-lg-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={closeNav}
              aria-hidden="true"
            />
          )}
        </AnimatePresence>
      </nav>

      {/* Inline styles scoped to Navbar */}
      <style>{`
        /* Overlay behind the opened mobile menu */
        .nav-overlay {
          position: fixed;
          inset: 0;
          background: rgba(2, 6, 23, 0.38);
          backdrop-filter: blur(2px);
          z-index: 1020; /* below navbar (bootstrap fixed-top is 1030) */
        }

        /* Nav link micro-interactions */
        .nav-right .nav-link {
          transition: background 160ms ease, color 160ms ease, transform 160ms ease;
        }
        .nav-right .nav-link:hover {
          background: rgba(13,110,253,0.08);
          color: var(--ibg-navy) !important;
          transform: translateY(-1px);
        }
        .nav-right .nav-link:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(13,110,253,0.18);
        }

        /* Portal button styling aligned with other pages */
        .portal-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 10px 14px;
          border-radius: 14px;
          font-weight: 900;
          font-size: 0.92rem;
          color: var(--ibg-white);
          background: linear-gradient(135deg, var(--ibg-navy) 0%, var(--ibg-blue) 60%, var(--ibg-blue-light) 100%);
          box-shadow: 0 18px 40px rgba(13,110,253,0.18);
          border: 1px solid rgba(255,255,255,0.18);
          transition: transform 150ms ease, box-shadow 150ms ease, opacity 150ms ease;
          text-decoration: none;
          white-space: nowrap;
        }
        .portal-btn:hover,
        .portal-btn:focus {
          transform: translateY(-2px);
          box-shadow: 0 22px 54px rgba(13,110,253,0.24);
          opacity: 0.98;
        }
        .portal-btn:active { transform: translateY(0); }

        .portal-btn-wrapper { padding-left: .75rem; padding-right: .75rem; }

        /* Mobile layout */
        @media (max-width: 991.98px) {
          .navbar-collapse {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            padding: 12px 12px 14px;
            background: rgba(255,255,255,0.92);
            border-bottom: 1px solid rgba(15, 23, 42, 0.08);
            box-shadow: 0 18px 50px rgba(15, 23, 42, 0.10);
            backdrop-filter: blur(14px);
            -webkit-backdrop-filter: blur(14px);
            z-index: 1031;
          }

          .nav-right {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 6px;
            align-items: stretch !important;
          }

          .nav-right .nav-link {
            padding: 12px 12px !important;
            text-align: left;
            width: 100%;
            border-radius: 16px;
          }

          .portal-btn {
            width: 100%;
            padding: 12px 12px;
            border-radius: 16px;
          }
        }

        /* Desktop spacing */
        @media (min-width: 992px) {
          .portal-btn-wrapper { margin-left: 8px; }
          .nav-right .nav-link { margin: 0 2px; }
        }
      `}</style>
    </>
  );
}

export default Navbar;